// FILE: client/src/screens/admin/categories/CreateSubcategoryModal.jsx

import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { adminCreateSubcategory } from "../../actions/subcategoryActions";
import Uploader from "../images/Uploader";
import { getApiErrorMessage } from "../../utils/errorMessages";

const toSlug = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const CreateSubcategoryModal = ({
  show,
  onHide,
  shopKey,
  parentId,
  categories = [],
  onCreated,
}) => {
  const dispatch = useDispatch();

  const [selectedParentId, setSelectedParentId] = useState(parentId || "");
  const [name, setName] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setSelectedParentId(parentId || "");
    }
  }, [parentId, show]);

  useEffect(() => {
    if (!show) {
      setName("");
      setIsPublished(true);
      setImages([]);
      setSubmitting(false);
      setError("");
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!shopKey || !selectedParentId || !name.trim()) {
      setError("Shop, parent category, and subcategory name are required.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const created = await dispatch(
        adminCreateSubcategory({
          shopKey,
          parentId: selectedParentId,
          name: name.trim(),
          slug: toSlug(name),
          isPublished,
          images,
        }),
      );

      toast.success("Subcategory created");
      onCreated?.(created);
      onHide?.();
    } catch (err) {
      setError(
        getApiErrorMessage(err, {
          fallback:
            "We could not create this subcategory right now. Please try again later.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Modal.Header closeButton>
          <Modal.Title>New Subcategory</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error ? <Alert variant="danger">{error}</Alert> : null}

          <Form.Group className="mb-3">
            <Form.Label>Parent category</Form.Label>
            <Form.Select
              value={selectedParentId}
              onChange={(e) => setSelectedParentId(e.target.value)}
              required
            >
              <option value="">Select parent category</option>

              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Published"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Subcategory images</Form.Label>
            <Uploader
              existingImages={images}
              setImageUrls={setImages}
              folder="subcategories"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={onHide}
            disabled={submitting}
          >
            Cancel
          </Button>

          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Create subcategory"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateSubcategoryModal;
